import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { CopyButton } from './CopyButton';

export interface DescriptionItem {
  label: ReactNode;
  /** null, undefined and '' show as "—". */
  value: ReactNode;
  /** Columns the item spans; `full` takes the whole row. */
  span?: 1 | 2 | 'full';
  /** Adds a copy button copying this text (IDs, UUIDs). */
  copy?: string | number | null;
  /** Hides the item (instead of `cond && {...}` juggling in the array). */
  hidden?: boolean;
}

// phones: two columns of short facts (one for `columns={2}`, which holds longer values)
const COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-2 xl:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
} as const;

/**
 * Label/value pairs in a responsive grid, label above value: the summary block of detail pages.
 */
export function DescriptionList({ items, columns = 3, className }: { items: DescriptionItem[]; columns?: keyof typeof COLUMNS; className?: string }) {
  const { t } = useTranslation();
  return (
    <dl className={cn('m-0 grid gap-x-4 gap-y-4 sm:gap-x-6', COLUMNS[columns], className)}>
      {items
        .filter((item) => !item.hidden)
        .map((item, i) => {
          const empty = item.value === null || item.value === undefined || item.value === '';
          return (
            <div key={i} className={cn('min-w-0', item.span === 2 && (columns >= 3 ? 'col-span-2' : 'sm:col-span-2'), item.span === 'full' && 'col-span-full')}>
              <dt className="text-xs font-bold text-ink-muted">{item.label}</dt>
              <dd className="m-0 mt-0.5 flex min-w-0 items-center gap-1 text-base text-ink">
                <span className="min-w-0 break-words">{empty ? <span className="text-ink-faint">—</span> : item.value}</span>
                {item.copy != null && item.copy !== '' && <CopyButton value={String(item.copy)} label={typeof item.label === 'string' ? t('actions.copyValue', { label: item.label }) : undefined} />}
              </dd>
            </div>
          );
        })}
    </dl>
  );
}
